import { useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Stack, IconButton } from '@fluentui/react'

import { AppStateContext } from '../../state/AppProvider'
import styles from './Help.module.css'

interface PromptExample {
  prompt: string
  description: string
}

interface PromptCategory {
  title: string
  icon: string
  examples: PromptExample[]
}

const promptCategories: PromptCategory[] = [
  {
    title: 'Dashboard & Overview',
    icon: 'ViewDashboard',
    examples: [
      { prompt: 'Show me the dashboard', description: 'Get an overview of candidates and clients' },
      { prompt: 'How many candidates do we have?', description: 'Get total candidate count' },
      { prompt: 'Give me candidate statistics', description: 'Detailed stats including recent activity' },
      { prompt: 'Show client statistics', description: 'Client counts and activity metrics' }
    ]
  },
  {
    title: 'Listing Records',
    icon: 'List',
    examples: [
      { prompt: 'List all candidates', description: 'Shows up to 30 candidates with basic details' },
      { prompt: 'List all clients', description: 'Shows up to 30 clients with basic details' },
      { prompt: 'Show me all jobs', description: 'List available job positions' },
      { prompt: 'List recent applications', description: 'View application records' }
    ]
  },
  {
    title: 'Searching',
    icon: 'Search',
    examples: [
      { prompt: 'Search for candidates with Python skills', description: 'Find candidates by skills' },
      { prompt: 'Find candidates in Sydney', description: 'Search by location' },
      { prompt: 'Search for John Smith', description: 'Find candidates by name' },
      { prompt: 'Find clients in the technology industry', description: 'Search clients by industry' },
      { prompt: 'Search for senior developer jobs', description: 'Find jobs by title or keywords' }
    ]
  },
  {
    title: 'Viewing Profiles',
    icon: 'ContactInfo',
    examples: [
      { prompt: 'Get candidate profile 123', description: 'View full candidate profile by ID' },
      { prompt: 'Show me the profile for candidate John Smith', description: 'View candidate profile by name' },
      { prompt: 'Get client profile 456', description: 'View client information' },
      { prompt: 'Show job details for position 789', description: 'View job requirements and details' },
      { prompt: 'What interviews are scheduled for candidate 123?', description: 'View interview history' }
    ]
  },
  {
    title: 'Creating Records',
    icon: 'AddFriend',
    examples: [
      { prompt: 'Create a new candidate named John Doe with email john@example.com', description: 'Add a new candidate' },
      { prompt: 'Add a new client called Tech Solutions', description: 'Create a client record' },
      { prompt: 'Create a job for Senior Developer at client 123', description: 'Add a new job position' },
      { prompt: 'Create an application for candidate 123 for job 456', description: 'Record a job application' },
      { prompt: 'Schedule an interview for candidate 123 on Monday at 2pm', description: 'Create an interview' }
    ]
  },
  {
    title: 'Updating Records',
    icon: 'Edit',
    examples: [
      { prompt: 'Update candidate 123 phone number to 0412345678', description: 'Modify candidate details' },
      { prompt: 'Change the status of application 456 to interviewed', description: 'Update application status' },
      { prompt: 'Update job 789 salary to $120,000', description: 'Modify job details' },
      { prompt: 'Add notes to client 123: Great to work with', description: 'Update client information' }
    ]
  },
  {
    title: 'Resume Generation',
    icon: 'Document',
    examples: [
      { prompt: 'Generate a resume for candidate 123', description: 'Create a formatted resume document' },
      { prompt: 'Create a resume for John Smith', description: 'Generate resume by candidate name' }
    ]
  },
  {
    title: 'Email',
    icon: 'Mail',
    examples: [
      { prompt: 'Email candidate 123 about an interview next Monday', description: 'Send email to a candidate by ID' },
      { prompt: 'Email client 456 with a project update', description: 'Send email to a client by ID' },
      { prompt: 'Email all candidates about the new job openings', description: 'Bulk email to all candidates' },
      { prompt: 'Email all clients with a newsletter', description: 'Bulk email to all clients' },
      { prompt: 'Show email history for candidate 123', description: 'View emails sent to a candidate' },
      { prompt: 'Show email history for client 456', description: 'View emails sent to a client' },
      { prompt: 'What is the email queue status?', description: 'Check queued, sending, sent and failed counts' },
      { prompt: 'Delete all queued emails for today', description: 'Remove unsent queued emails for a date (sent emails are never deleted)' }
    ]
  },
  {
    title: 'Interviews',
    icon: 'Calendar',
    examples: [
      { prompt: 'Schedule an interview for candidate 123 on Monday at 2pm', description: 'Create a new interview' },
      { prompt: 'What interviews are scheduled for candidate 123?', description: 'View interview history' },
      { prompt: 'Update interview 456 outcome to "Passed" with notes "Strong technical skills"', description: 'Set interview outcome and notes' },
      { prompt: 'Update interview 456 notes to "Needs follow-up on references"', description: 'Add or update interview notes' }
    ]
  },
  {
    title: 'Contacts & Communication',
    icon: 'People',
    examples: [
      { prompt: 'Show contacts for client 123', description: 'View client contact persons' },
      { prompt: 'Add a contact for client 456: Jane Doe, HR Manager', description: 'Create a new contact' },
      { prompt: 'Who is the main contact at ABC Corp?', description: 'Find primary contact' }
    ]
  }
]

const Help = () => {
  const navigate = useNavigate()
  const appStateContext = useContext(AppStateContext)

  useEffect(() => {
    appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: 'User is viewing the Help page.' })
    return () => { appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: '' }) }
  }, [])

  const handlePromptClick = (prompt: string) => {
    // Navigate back to chat - the prompt could be copied to clipboard
    navigator.clipboard.writeText(prompt)
    navigate('/')
  }

  return (
    <div className={styles.container}>
      <Stack horizontal verticalAlign="center" className={styles.header}>
        <IconButton
          iconProps={{ iconName: 'Back' }}
          title="Back to chat"
          onClick={() => navigate('/')}
          className={styles.backButton}
        />
        <h1 className={styles.title}>How to Use the Chat Assistant</h1>
      </Stack>

      <p className={styles.intro}>
        Click any example prompt to copy it to your clipboard, then paste it into the chat.
        These are just examples - you can phrase your requests naturally!
      </p>

      <div className={styles.categoriesGrid}>
        {promptCategories.map((category, index) => (
          <div key={index} className={styles.category}>
            <Stack horizontal verticalAlign="center" className={styles.categoryHeader}>
              <IconButton
                iconProps={{ iconName: category.icon }}
                className={styles.categoryIcon}
                disabled
              />
              <h2 className={styles.categoryTitle}>{category.title}</h2>
            </Stack>
            <ul className={styles.promptList}>
              {category.examples.map((example, idx) => (
                <li key={idx} className={styles.promptItem}>
                  <button
                    className={styles.promptButton}
                    onClick={() => handlePromptClick(example.prompt)}
                    title="Click to copy"
                  >
                    <span className={styles.promptText}>"{example.prompt}"</span>
                    <span className={styles.promptDescription}>{example.description}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className={styles.tips}>
        <h2 className={styles.tipsTitle}>Tips</h2>
        <ul className={styles.tipsList}>
          <li>You can use natural language - no need to follow exact formats</li>
          <li>Reference records by ID number or by name</li>
          <li>Ask follow-up questions about results</li>
          <li>Request multiple things in one message</li>
          <li>Ask "what can you do?" for a quick summary</li>
        </ul>
      </div>
    </div>
  )
}

export default Help
